import useApi from "@/hooks/useApi";
import useTeams from "@/hooks/useTeams";
import React, { useEffect, useMemo, useState } from "react";
import {
  IvrIntegrationModel,
  PronunciationLexicon,
  IVRVoiceConfiguration
} from "../types";
import SmsIntegrationEditModal from "./SmsIntegrationEditModal";
import SmsIntegrationSetupModal from "./SmsIntegrationSetupModal";
import IvrIntegrationEditModal from "./IvrIntegrationEditModal";
import {
  PrimaryBlueButton, SecondaryBlueButton, IconPlus, Column, TableBase, Severity
} from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
import PronunciationActionBar from "./PronunciationActionBar";
import PronunciationLexiconModal from "./PronunciationLexiconModal";
import IVRVoiceConfigurationModal from "./IVRVoiceConfigurationModal";
import useToastAlert from "@/hooks/useToastAlert";
import { WorkflowListItem } from "../Workflows/types";
import StarterWorkflowWizard from "../Integrations/StarterWorkflowWizard";
import LaunchWizardButton from "@/components/LaunchWizardButton";

// ── Types ─────────────────────────────────────────────────────────────────────

interface IvrBlockedNumber {
  id: number;
  phoneNumber: string;
  reason?: string;
  expiresAt?: string;
  createdAt: string;
  createdBy?: string;
}

interface RecentBlockedCall {
  id: number;
  message: string;
  details?: string;
  timestamp: string;
}

// ── Workflow health types ─────────────────────────────────────────────────────

type WorkflowStatus = "active" | "disabled" | "missing" | "auto";

function resolveWorkflowStatus(integration: IvrIntegrationModel): WorkflowStatus {
  if (!integration.workflowId) return "auto";
  if (!integration.workflowExists) return "missing";
  if (integration.workflowEnabled === false) return "disabled";
  return "active";
}

function WorkflowStatusBadge({ status }: { status: WorkflowStatus }) {
  const styles: Record<WorkflowStatus, string> = {
    active: "bg-green-50 text-green-700 border border-green-200",
    disabled: "bg-amber-50 text-amber-700 border border-amber-200",
    missing: "bg-red-50 text-red-700 border border-red-200",
    auto: "bg-slate-100 text-slate-500 border border-slate-200"
  };
  const labels: Record<WorkflowStatus, string> = {
    active: "Active",
    disabled: "Disabled ⚠",
    missing: "Deleted ✕",
    auto: "Auto-detect"
  };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

// ── Table column definitions ──────────────────────────────────────────────────

const pronunciationColumns: Column[] = [
  { label: "Written Word or Phrase", accessor: "misrecognitions" },
  { label: "Spoken Pronunciation", accessor: "correctedText" },
  { label: "Type", accessor: "type" },
  { label: "Team", accessor: "team" },
  { label: "Active", accessor: "isActive" },
  { label: "Actions", accessor: "actions", disableSort: true }
];

const voiceConfigurationColumns: Column[] = [
  { label: "Configuration Name", accessor: "name" },
  { label: "Voice Provider", accessor: "voiceProvider" },
  { label: "Voice ID", accessor: "voiceId" },
  { label: "Team", accessor: "team" },
  { label: "Actions", accessor: "actions", disableSort: true }
];

// ── Component ─────────────────────────────────────────────────────────────────

export default function IvrIntegration() {
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState<IvrIntegrationModel[]>([]);
  const [editedIntegrationId, setEditedIntegrationId] = useState<number>();
  const [editError, setEditError] = useState("");
  const api = useApi();

  // Site-level default workflow
  const [defaultWorkflowId, setDefaultWorkflowId] = useState<number | undefined>();
  const [defaultWorkflowName, setDefaultWorkflowName] = useState<string | undefined>();
  const [allWorkflows, setAllWorkflows] = useState<WorkflowListItem[]>([]);
  const [savingDefault, setSavingDefault] = useState(false);

  // Pronunciation Lexicons state
  const [lexicons, setLexicons] = useState<PronunciationLexicon[]>([]);
  const [showLexiconModal, setShowLexiconModal] = useState(false);
  const [editedLexicon, setEditedLexicon] = useState<PronunciationLexicon | undefined>(undefined);

  // Voice Configurations state
  const [voiceConfigurations, setVoiceConfigurations] = useState<IVRVoiceConfiguration[]>([]);
  const [showVoiceConfigModal, setShowVoiceConfigModal] = useState(false);
  const [editedVoiceConfiguration, setEditedVoiceConfiguration] = useState<IVRVoiceConfiguration | undefined>(undefined);

  // Starter Workflow Wizard
  const [showWizard, setShowWizard] = useState(false);

  // Blocked Numbers state
  const [blockedNumbers, setBlockedNumbers] = useState<IvrBlockedNumber[]>([]);
  const [blockFormPhone, setBlockFormPhone] = useState("");
  const [blockFormReason, setBlockFormReason] = useState("");
  const [blockFormExpiry, setBlockFormExpiry] = useState("");
  const [blockFormSaving, setBlockFormSaving] = useState(false);
  const [recentBlockedCalls, setRecentBlockedCalls] = useState<RecentBlockedCall[]>([]);
  const [showRecentCalls, setShowRecentCalls] = useState(false);

  // Rate-limited numbers state
  const [rateLimitedCalls, setRateLimitedCalls] = useState<RecentBlockedCall[]>([]);
  const [showRateLimited, setShowRateLimited] = useState(false);
  const [resettingRateLimit, setResettingRateLimit] = useState<string | null>(null);

  const { displayToast } = useToastAlert();
  const { getTeamNameById } = useTeams();

  const fetchIntegrations = React.useCallback(async () => {
    try {
      const response = await api.messaging.get("Sms/Integrations");
      const data = await response.json();
      setIntegrations(
        Array.isArray(data) ? data.filter((i: IvrIntegrationModel) => i.isIvr) : []
      );
    } catch {
      setIntegrations([]);
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  const fetchLexicons = React.useCallback(async () => {
    try {
      const response = await api.messaging.get("PronunciationLexicons");
      const data = await response.json();
      setLexicons(Array.isArray(data) ? data : []);
    } catch {
      setLexicons([]);
    }
  }, [api.messaging]);

  const fetchVoiceConfigurations = React.useCallback(async () => {
    try {
      const response = await api.messaging.get("IVRVoiceConfigurations");
      const data = await response.json();
      setVoiceConfigurations(Array.isArray(data) ? data : []);
    } catch {
      setVoiceConfigurations([]);
    }
  }, [api.messaging]);

  const fetchSiteConfig = React.useCallback(async () => {
    try {
      const response = await api.messaging.get("Sms/Integrations/SiteConfig");
      const data = await response.json();
      setDefaultWorkflowId(data?.defaultWorkflowId ?? undefined);
      setDefaultWorkflowName(data?.defaultWorkflowName ?? undefined);
    } catch {
      // non-fatal
    }
  }, [api.messaging]);

  const fetchWorkflows = React.useCallback(async () => {
    try {
      const response = await api.messaging.get("Workflows/List");
      const data = await response.json();
      setAllWorkflows(Array.isArray(data) ? data : []);
    } catch {
      setAllWorkflows([]);
    }
  }, [api.messaging]);

  const fetchBlockedNumbers = React.useCallback(async () => {
    try {
      const response = await api.messaging.get("IvrBlockedNumbers");
      const data = await response.json();
      setBlockedNumbers(Array.isArray(data) ? data : []);
    } catch {
      setBlockedNumbers([]);
    }
  }, [api.messaging]);

  const fetchRecentBlockedCalls = React.useCallback(async () => {
    try {
      const response = await api.messaging.get("IvrBlockedNumbers/RecentCalls");
      const data = await response.json();
      setRecentBlockedCalls(Array.isArray(data) ? data : []);
    } catch {
      setRecentBlockedCalls([]);
    }
  }, [api.messaging]);

  const fetchRateLimitedCalls = React.useCallback(async () => {
    try {
      const response = await api.messaging.get("IvrBlockedNumbers/rate-limited");
      const data = await response.json();
      setRateLimitedCalls(Array.isArray(data) ? data : []);
    } catch {
      setRateLimitedCalls([]);
    }
  }, [api.messaging]);

  useEffect(() => {
    fetchIntegrations();
    fetchLexicons();
    fetchVoiceConfigurations();
    fetchSiteConfig();
    fetchWorkflows();
    fetchBlockedNumbers();
    fetchRecentBlockedCalls();
    fetchRateLimitedCalls();

    const handleRefresh = () => fetchIntegrations();
    window.addEventListener("refreshIvrIntegrations", handleRefresh);
    return () => window.removeEventListener("refreshIvrIntegrations", handleRefresh);
  }, [fetchIntegrations, fetchLexicons, fetchVoiceConfigurations, fetchSiteConfig, fetchWorkflows, fetchBlockedNumbers, fetchRecentBlockedCalls, fetchRateLimitedCalls]);

  const errorMap: Record<string, string> = {
    release_error: "An error occurred while releasing the phone number.",
    update_error: "An error occurred while updating the phone number."
  };

  const editedIntegration = useMemo(
    () => integrations.find((x) => x.id === editedIntegrationId),
    [editedIntegrationId, integrations]
  );

  const lexiconTableData = useMemo(() => {
    return lexicons.map((lexicon) => ({
      ...lexicon,
      team: lexicon.teamId ? getTeamNameById(Number(lexicon.teamId)) : "All Teams",
      isActive: lexicon.isActive ? "Yes" : "No",
      misrecognitions:
        lexicon.misrecognitions.length > 50
          ? lexicon.misrecognitions.substring(0, 50) + "..."
          : lexicon.misrecognitions,
      actions: (
        <PronunciationActionBar
          onEdit={() => handleEditLexicon(lexicon)}
          onDelete={() => deleteLexicon(lexicon.id)}
        />
      )
    }));
  }, [lexicons, getTeamNameById]);

  const voiceConfigurationTableData = useMemo(() => {
    return voiceConfigurations.map((config) => ({
      ...config,
      team: config.teamId ? getTeamNameById(Number(config.teamId)) : "All Teams",
      actions: (
        <PronunciationActionBar
          onEdit={() => handleEditVoiceConfiguration(config)}
          onDelete={() => deleteVoiceConfiguration(config.id)}
        />
      )
    }));
  }, [voiceConfigurations, getTeamNameById]);

  if (loading) return <div className="flex justify-center py-20"><LoadingSpinner /></div>;

  // Count integrations with workflow problems for the warning banner
  const brokenCount = integrations.filter((i) => {
    const s = resolveWorkflowStatus(i);
    return s === "disabled" || s === "missing";
  }).length;

  async function release() {
    setLoading(true);
    if (confirm("Are you sure you want to remove this integration?")) {
      try {
        setIntegrations(integrations.filter((i) => i.id !== editedIntegrationId));
        const resp = await api.messaging.delete(`Sms/Integrations/${editedIntegrationId}`);
        if (resp.ok) {
          setEditedIntegrationId(undefined);
          setEditError("");
        }
      } catch {
        setEditError(errorMap["release_error"]);
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }

  async function update(integration: IvrIntegrationModel) {
    setLoading(true);
    try {
      const newIntegrations = [...integrations];
      const index = integrations.findIndex((x) => x.id === editedIntegrationId);
      newIntegrations[index] = integration;
      setIntegrations(newIntegrations);

      const resp = await api.messaging.post("Sms/Integrations/UpdatePhoneNumber", {
        Id: editedIntegrationId,
        TeamId: integration.teamId,
        Label: integration.label,
        IVRVoiceConfigurationId: integration.ivrVoiceConfigurationId,
        WorkflowId: integration.workflowId,
        FallbackTransferNumber: integration.fallbackTransferNumber
      });

      if (resp.ok) {
        setEditedIntegrationId(undefined);
        setEditError("");
        // Refresh to get updated workflow health fields
        fetchIntegrations();
        displayToast(Severity.Success, "IVR number updated.");
      } else {
        setEditError(errorMap["update_error"]);
      }
    } catch {
      setEditError(errorMap["update_error"]);
    } finally {
      setLoading(false);
    }
  }

  async function saveDefaultWorkflow(workflowId: number | undefined) {
    setSavingDefault(true);
    try {
      const resp = await api.messaging.post("Sms/Integrations/SiteConfig", {
        DefaultWorkflowId: workflowId ?? null
      });
      if (resp.ok) {
        setDefaultWorkflowId(workflowId);
        const wf = allWorkflows.find((w) => w.id === workflowId);
        setDefaultWorkflowName(wf?.name);
        displayToast(Severity.Success, "Default workflow saved.");
      } else {
        displayToast(Severity.Error, "Failed to save default workflow.");
      }
    } catch {
      displayToast(Severity.Error, "Failed to save default workflow.");
    } finally {
      setSavingDefault(false);
    }
  }

  async function deleteLexicon(id: number) {
    if (confirm("Are you sure you want to delete this pronunciation correction?")) {
      try {
        const resp = await api.messaging.delete(`PronunciationLexicons/${id}`);
        if (resp.ok) {
          setLexicons(lexicons.filter((x) => x.id !== id));
          displayToast(Severity.Success, "Pronunciation correction deleted.");
        } else {
          displayToast(Severity.Error, "Failed to delete pronunciation correction.");
        }
      } catch {
        displayToast(Severity.Error, "An error occurred while deleting.");
      }
    }
  }

  function handleEditLexicon(lexicon: PronunciationLexicon) {
    setEditedLexicon(lexicon);
    setShowLexiconModal(true);
  }

  function handleSaveLexicon(lexicon: PronunciationLexicon) {
    if (editedLexicon) {
      setLexicons(lexicons.map((x) => (x.id === lexicon.id ? lexicon : x)));
    } else {
      setLexicons([...lexicons, lexicon]);
    }
    setEditedLexicon(undefined);
  }

  async function deleteVoiceConfiguration(id: number) {
    if (confirm("Are you sure you want to delete this voice configuration?")) {
      try {
        const resp = await api.messaging.delete(`IVRVoiceConfigurations/${id}`);
        if (resp.ok) {
          setVoiceConfigurations(voiceConfigurations.filter((x) => x.id !== id));
          displayToast(Severity.Success, "Voice configuration deleted.");
        } else {
          displayToast(Severity.Error, "Failed to delete voice configuration.");
        }
      } catch {
        displayToast(Severity.Error, "An error occurred while deleting.");
      }
    }
  }

  function handleEditVoiceConfiguration(config: IVRVoiceConfiguration) {
    setEditedVoiceConfiguration(config);
    setShowVoiceConfigModal(true);
  }

  function handleSaveVoiceConfiguration(config: IVRVoiceConfiguration) {
    if (editedVoiceConfiguration) {
      setVoiceConfigurations(voiceConfigurations.map((x) => (x.id === config.id ? config : x)));
    } else {
      setVoiceConfigurations([...voiceConfigurations, config]);
    }
    setEditedVoiceConfiguration(undefined);
  }

  async function handleBlockNumber() {
    const phone = blockFormPhone.trim();
    if (!phone) return;
    setBlockFormSaving(true);
    try {
      const resp = await api.messaging.post("IvrBlockedNumbers", {
        phoneNumber: phone,
        reason: blockFormReason.trim() || null,
        expiresAt: blockFormExpiry || null
      });
      if (resp.ok) {
        const created = await resp.json();
        setBlockedNumbers((prev) => [created, ...prev]);
        setBlockFormPhone("");
        setBlockFormReason("");
        setBlockFormExpiry("");
        displayToast(Severity.Success, `${phone} has been blocked.`);
      } else {
        displayToast(Severity.Error, "Failed to block number.");
      }
    } catch {
      displayToast(Severity.Error, "An error occurred while blocking the number.");
    } finally {
      setBlockFormSaving(false);
    }
  }

  async function handleUnblock(id: number, phone: string) {
    try {
      const resp = await api.messaging.delete(`IvrBlockedNumbers/${id}`);
      if (resp.ok) {
        setBlockedNumbers((prev) => prev.filter((x) => x.id !== id));
        displayToast(Severity.Success, `${phone} has been unblocked.`);
      } else {
        displayToast(Severity.Error, "Failed to unblock number.");
      }
    } catch {
      displayToast(Severity.Error, "An error occurred while unblocking.");
    }
  }

  async function handleResetRateLimit(phone: string) {
    const normalized = phone.replace(/[^+\d]/g, "");
    setResettingRateLimit(normalized);
    try {
      const resp = await api.messaging.delete(`IvrBlockedNumbers/rate-limit/${encodeURIComponent(normalized)}`);
      if (resp.ok) {
        displayToast(Severity.Success, `Rate limit cleared for ${phone}. They can call again immediately.`);
        await fetchRateLimitedCalls();
      } else {
        displayToast(Severity.Error, "Failed to reset rate limit.");
      }
    } catch {
      displayToast(Severity.Error, "An error occurred while resetting rate limit.");
    } finally {
      setResettingRateLimit(null);
    }
  }

  return (
    <>
      {/* ── IVR Phone Numbers ─────────────────────────────────────────────── */}
      <div className="my-5">
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="text-lg font-semibold text-slate-800">IVR Phone Numbers</div>
            <div className="text-sm text-slate-500 mt-0.5">
              Each number routes callers to a workflow. Click Edit to assign or change a
              workflow, voice configuration, or fallback transfer number.
            </div>
          </div>
          <LaunchWizardButton onClick={() => setShowWizard(true)} />
        </div>

        {/* Broken-workflow banner */}
        {brokenCount > 0 && (
          <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800 flex items-start gap-2">
            <span className="mt-0.5 text-amber-500 flex-shrink-0">⚠</span>
            <div>
              <strong>{brokenCount} number{brokenCount > 1 ? "s have" : " has"} a workflow problem.</strong>{" "}
              Numbers with a <span className="font-semibold text-amber-700">Disabled</span> or{" "}
              <span className="font-semibold text-red-700">Deleted</span> workflow will answer
              calls but play an error message instead of running automation. Click Edit on the
              affected number to fix it.
            </div>
          </div>
        )}

        {/* Default workflow selector */}
        <div className="mb-5 p-4 bg-slate-50 border border-slate-200 rounded-lg">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-700 mb-0.5">
                Default Workflow
              </div>
              <div className="text-xs text-slate-500">
                Used by any number that has no explicit workflow assigned.
                Setting a default here removes the need to assign a workflow to every number.
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <select
                className="input text-sm"
                value={defaultWorkflowId ?? ""}
                onChange={(e) => {
                  const val = e.currentTarget.value ? Number(e.currentTarget.value) : undefined;
                  saveDefaultWorkflow(val);
                }}
                disabled={savingDefault}
                style={{ minWidth: 220 }}
              >
                <option value="">— No default (auto-detect) —</option>
                {allWorkflows.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
              {savingDefault && (
                <span className="text-xs text-slate-400">Saving…</span>
              )}
            </div>
          </div>
        </div>

        {/* Numbers table */}
        {integrations.length === 0 ? (
          <div className="text-sm text-slate-500 py-4">
            No IVR numbers configured yet. Purchase a number from the SMS Integrations section
            and mark it as IVR.
          </div>
        ) : (
          <div className="overflow-auto">
            <table className="w-full bg-white border border-slate-200 rounded-lg overflow-hidden text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Phone Number</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Team</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Workflow</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Status</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-600 w-1"></th>
                </tr>
              </thead>
              <tbody>
                {integrations.map((integration) => {
                  const status = resolveWorkflowStatus(integration);
                  return (
                    <tr
                      key={integration.id}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="font-mono text-slate-700">{integration.phoneNumber}</div>
                        {integration.label && (
                          <div className="text-xs text-slate-400 mt-0.5">{integration.label}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {getTeamNameById(Number(integration.teamId)) ?? "—"}
                      </td>
                      <td className="px-4 py-3">
                        {integration.workflowId ? (
                          <div>
                            <div className="font-medium text-slate-700">
                              {integration.workflowName ?? `Workflow #${integration.workflowId}`}
                            </div>
                            {status === "disabled" && (
                              <div className="text-xs text-amber-600 mt-0.5">
                                Workflow is disabled — turn it on in Messaging → Workflows,
                                or reassign below.
                              </div>
                            )}
                            {status === "missing" && (
                              <div className="text-xs text-red-600 mt-0.5">
                                Workflow was deleted — please assign a replacement.
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-xs">
                            {defaultWorkflowId
                              ? `Site default: ${defaultWorkflowName ?? `#${defaultWorkflowId}`}`
                              : "Auto-detect"}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <WorkflowStatusBadge status={status} />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <SecondaryBlueButton
                          label="Edit"
                          onClick={() => setEditedIntegrationId(integration.id)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Pronunciation Corrections ─────────────────────────────────────── */}
      <div className="my-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="text-lg font-semibold text-slate-800">
              IVR Pronunciation Corrections
            </div>
            <div className="text-sm text-slate-500 mt-0.5">
              Teach the IVR voice agent how to pronounce brand names, product names, or unusual
              words correctly during calls. Optionally also improves speech recognition when
              callers say those terms.
            </div>
          </div>
          <SecondaryBlueButton
            prefixIcon={<IconPlus />}
            onClick={() => {
              setEditedLexicon(undefined);
              setShowLexiconModal(true);
            }}
            label="Add Correction"
          />
        </div>
        <div className="overflow-auto">
          <TableBase columns={pronunciationColumns} data={lexiconTableData} />
        </div>
      </div>

      {/* ── Voice Configurations ──────────────────────────────────────────── */}
      <div className="my-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="text-lg font-semibold text-slate-800">IVR Voice Configurations</div>
            <div className="text-sm text-slate-500 mt-0.5">
              Set the voice, language, and caller experience for each team or number.
              Assign a configuration to a phone number via Edit above.
            </div>
          </div>
          <SecondaryBlueButton
            prefixIcon={<IconPlus />}
            onClick={() => {
              setEditedVoiceConfiguration(undefined);
              setShowVoiceConfigModal(true);
            }}
            label="Add Configuration"
          />
        </div>
        <div className="overflow-auto">
          <TableBase columns={voiceConfigurationColumns} data={voiceConfigurationTableData} />
        </div>

        {/* Velaro fine-tuning note */}
        <div className="mt-4 p-4 bg-blue-50 border border-blue-100 rounded-lg flex gap-3">
          <div className="flex-shrink-0 text-blue-400 text-lg mt-0.5">ℹ</div>
          <div>
            <div className="text-sm font-semibold text-blue-800 mb-1">
              Velaro fine-tunes your IVR for you
            </div>
            <p className="text-xs text-blue-700 leading-relaxed">
              The settings above are pre-configured with industry-best defaults that work well
              for the vast majority of callers. Under the hood, Velaro continuously monitors
              call quality and can make deeper adjustments — for example, optimizing for
              mobile callers with variable signal, high-background-noise environments such as
              retail floors or warehouses, callers who speak more slowly or with regional accents,
              or businesses with very short expected call durations. Every account and caller
              experience is different, and there is no one-size-fits-all.{" "}
              <strong>
                If you notice a recurring issue — responses feel slow, the bot mishears certain
                words, or calls end unexpectedly — reach out to{" "}
                <a href="mailto:support@velaro.com" className="underline">
                  support@velaro.com
                </a>{" "}
                and we can review and tune your specific configuration.
              </strong>
            </p>
          </div>
        </div>
      </div>

      {/* ── Blocked Numbers ───────────────────────────────────────────────── */}
      <div className="my-8">
        <div className="mb-4">
          <div className="text-lg font-semibold text-slate-800">IVR Blocked Numbers</div>
          <div className="text-sm text-slate-500 mt-0.5">
            Callers on this list hear a polite rejection message and are immediately disconnected.
            Use this for spam, abusive callers, or TCPA opt-outs.
          </div>
        </div>

        {/* Add form */}
        <div className="mb-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
          <div className="text-sm font-semibold text-slate-700 mb-3">Block a Number</div>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-500 font-medium">Phone Number (E.164)</label>
              <input
                type="text"
                className="input text-sm"
                placeholder="+15551234567"
                value={blockFormPhone}
                onChange={(e) => setBlockFormPhone(e.currentTarget.value)}
                style={{ minWidth: 180 }}
              />
            </div>
            <div className="flex flex-col gap-1 flex-1" style={{ minWidth: 200 }}>
              <label className="text-xs text-slate-500 font-medium">Reason (optional)</label>
              <input
                type="text"
                className="input text-sm"
                placeholder="e.g. Abusive caller, spam"
                value={blockFormReason}
                onChange={(e) => setBlockFormReason(e.currentTarget.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-500 font-medium">Expires (optional)</label>
              <input
                type="date"
                className="input text-sm"
                value={blockFormExpiry}
                onChange={(e) => setBlockFormExpiry(e.currentTarget.value)}
              />
            </div>
            <PrimaryBlueButton
              label={blockFormSaving ? "Blocking…" : "Block Number"}
              onClick={handleBlockNumber}
              disabled={!blockFormPhone.trim() || blockFormSaving}
            />
          </div>
        </div>

        {/* Blocked numbers list */}
        {blockedNumbers.length === 0 ? (
          <div className="text-sm text-slate-400 py-4">No numbers are currently blocked.</div>
        ) : (
          <div className="overflow-auto max-h-80">
            <table className="w-full bg-white border border-slate-200 rounded-lg overflow-hidden text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Phone Number</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Reason</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Expires</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Blocked By</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Blocked On</th>
                  <th className="px-4 py-2.5 w-1"></th>
                </tr>
              </thead>
              <tbody>
                {blockedNumbers.map((b) => (
                  <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-slate-700">{b.phoneNumber}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{b.reason ?? <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {b.expiresAt
                        ? new Date(b.expiresAt).toLocaleDateString()
                        : <span className="text-slate-400">Permanent</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{b.createdBy ?? "—"}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {new Date(b.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button
                        className="text-xs text-red-600 hover:text-red-800 font-medium border border-red-200 hover:border-red-400 rounded px-2 py-1 transition-colors bg-red-50 hover:bg-red-100"
                        onClick={() => handleUnblock(b.id, b.phoneNumber)}
                        aria-label={`Unblock ${b.phoneNumber}`}
                      >
                        Unblock
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Recent Blocked Call Events ────────────────────────────────────── */}
      {recentBlockedCalls.length > 0 && (
        <div className="mt-3">
          <button
            className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
            onClick={() => setShowRecentCalls((v) => !v)}
            aria-expanded={showRecentCalls}
          >
            <span>{showRecentCalls ? "▾" : "▸"}</span>
            <span>Recent blocked call events ({recentBlockedCalls.length})</span>
          </button>
          {showRecentCalls && (
            <div className="mt-2 overflow-auto max-h-64 border border-slate-200 rounded-lg bg-white">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-slate-600">Event</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-600">Details</th>
                    <th className="px-3 py-2 text-left font-semibold text-slate-600 whitespace-nowrap">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {recentBlockedCalls.map((c) => (
                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 text-slate-700">{c.message}</td>
                      <td className="px-3 py-2 text-slate-500 font-mono">{c.details ?? "—"}</td>
                      <td className="px-3 py-2 text-slate-400 whitespace-nowrap">
                        {new Date(c.timestamp).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Rate-Limited Numbers ──────────────────────────────────────────── */}
      <div className="mt-4">
        <button
          className="text-xs text-amber-600 hover:text-amber-800 flex items-center gap-1 font-medium"
          onClick={() => setShowRateLimited((v) => !v)}
          aria-expanded={showRateLimited}
        >
          <span>{showRateLimited ? "▾" : "▸"}</span>
          <span>
            Rate-limited callers{rateLimitedCalls.length > 0 ? ` (${rateLimitedCalls.length} recent events)` : ""}
          </span>
        </button>
        {showRateLimited && (
          <div className="mt-2">
            <p className="text-xs text-slate-500 mb-2">
              Numbers that triggered the spam rate limit (more than 15 calls in 30 minutes).
              Rate limits clear automatically after 30 minutes, or you can reset them manually below.
            </p>
            {rateLimitedCalls.length === 0 ? (
              <div className="text-sm text-slate-400 py-3">No rate-limited callers in recent history.</div>
            ) : (
              <div className="overflow-auto max-h-64 border border-amber-200 rounded-lg bg-white">
                <table className="w-full text-xs">
                  <thead className="bg-amber-50 border-b border-amber-200">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-amber-700">Event</th>
                      <th className="px-3 py-2 text-left font-semibold text-amber-700 whitespace-nowrap">Time</th>
                      <th className="px-3 py-2 w-1"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rateLimitedCalls.map((c) => {
                      const phoneMatch = c.message.match(/from (\+[\d]+|\d+)/);
                      const phone = phoneMatch ? phoneMatch[1] : null;
                      return (
                        <tr key={c.id} className="border-b border-amber-100 hover:bg-amber-50">
                          <td className="px-3 py-2 text-slate-700">{c.message}</td>
                          <td className="px-3 py-2 text-slate-400 whitespace-nowrap">
                            {new Date(c.timestamp).toLocaleString()}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {phone && (
                              <button
                                className="text-xs text-amber-700 hover:text-amber-900 font-medium border border-amber-300 hover:border-amber-500 rounded px-2 py-1 transition-colors bg-amber-50 hover:bg-amber-100 disabled:opacity-50"
                                onClick={() => handleResetRateLimit(phone)}
                                disabled={resettingRateLimit === phone.replace(/[^+\d]/g, "")}
                              >
                                {resettingRateLimit === phone.replace(/[^+\d]/g, "") ? "Resetting…" : "Reset"}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Modals ────────────────────────────────────────────────────────── */}
      {editedIntegration && (
        <IvrIntegrationEditModal
          onClose={() => {
            setEditedIntegrationId(undefined);
            setEditError("");
          }}
          release={release}
          update={update}
          integration={editedIntegration}
          error={editError}
        />
      )}
      {showLexiconModal && (
        <PronunciationLexiconModal
          show={showLexiconModal}
          onClose={() => {
            setShowLexiconModal(false);
            setEditedLexicon(undefined);
          }}
          onSave={handleSaveLexicon}
          existingLexicon={editedLexicon}
        />
      )}
      {showVoiceConfigModal && (
        <IVRVoiceConfigurationModal
          show={showVoiceConfigModal}
          onClose={() => {
            setShowVoiceConfigModal(false);
            setEditedVoiceConfiguration(undefined);
          }}
          onSave={handleSaveVoiceConfiguration}
          existingConfiguration={editedVoiceConfiguration}
        />
      )}

      {showWizard && (
        <StarterWorkflowWizard
          integration="ivr"
          onClose={() => setShowWizard(false)}
        />
      )}
    </>
  );
}
